import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {v4 as uuidv4} from "uuid";
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  useToast,
  VStack,
  useBreakpointValue,
  SimpleGrid,
  Image,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import {
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  Stepper,
  useSteps,
} from '@chakra-ui/react'
import {submitForm, validateUrlTempForms} from "../authRequests";
import {crud} from "../crudRequests";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import Signature from "./Froms/Signature";
import SignatureModal from "./Froms/SignatureModal";
import FormStripeCard from "./Information/CC/FormStripeCard";
import {normalizeDateInput} from "../utils/dateValidation";
import {
  fieldHasValue,
  toggleCheckboxInput,
} from "../utils/formTreeUtils";
import {prefillSectionsWithPatientData} from "../utils/formPrefillUtils";
import {reportError} from "../lib/reportError";
import {
  FORM_RESULT_STATES,
  isInMobileApp,
  reportFormResultToMobileApp,
} from "../lib/mobileAppBridge";
import {provinceOptions} from "../utils/locationOptions";
import {APP_LOCALE} from "../utils/appLocale";

const TEXT_ONLY_ELEMENTS = new Set(["span", "label", "p", "h1", "h2", "h3", "h4", "h5", "h6"]);

// What proves this browser may write to this submission.
//
// The emailed link carries its encrypted params in the URL; the same form
// opened inside the portal carries a session instead. One or the other — the
// server accepts no third option, and used to accept none at all.
// What the server said, and whether pressing the button again could help.
//
// Every failure used to read the same: "We couldn't submit your form. Your
// answers are still here — please try again." That sentence is right for a
// network blip and wrong for everything else. A patient whose link has expired
// can press it a hundred times; a patient whose form is already completed has
// nothing wrong with them at all. Telling them to retry something that cannot
// work is how a support call becomes an angry one.
//
// The codes come from `src/forms/formLinkAuthority.js`, which is the one place
// the rules live. Anything unrecognised keeps the old sentence, because an
// unknown failure IS worth retrying.
const TERMINAL_SUBMISSION_CODES = {
  ALREADY_COMPLETED: {
    title: "Already submitted",
    description: "This form has already been completed. There is nothing left to do.",
    status: "info",
    done: true,
  },
  LINK_EXPIRED: {
    title: "This link has expired",
    description:
      "Please ask your clinic to send you a new link. Your answers are saved on this device and will still be here.",
    status: "warning",
  },
  LINK_SUPERSEDED: {
    title: "This link is out of date",
    description:
      "Your clinic sent a newer link for this form — please open the most recent email. Your answers are saved on this device.",
    status: "warning",
  },
  FORBIDDEN: {
    title: "Not allowed",
    description: "You do not have permission to complete this form for this person.",
    status: "error",
  },
};

function describeSubmissionFailure(error) {
  const body = error?.response?.data;
  const code = typeof body === "object" && body ? body.error : null;
  const known = code ? TERMINAL_SUBMISSION_CODES[code] : null;
  if (known) return known;

  return {
    title: "Error",
    description:
      "We couldn't submit your form. Your answers are still here — please try again.",
    status: "error",
  };
}

function submitProof(data) {
  const raw = window.location.href.split("?params=")[1];
  if (raw) return {params: decodeURIComponent(raw)};
  if (data?.state?.jwt) return {jwt: data.state.jwt};
  return {};
}

const PROVINCES = provinceOptions.map((p) => p.value);
const LOCAL_DRAFT_PREFIX = "brightlight-temp-form-draft";
const MAX_FORM_ROW_COLUMNS = 3;

// Resolve placeholder select options like [List of provinces] / [List of insurers]
function resolveDynamicOptions(sections, dynamicLists) {
  if (!sections || !Array.isArray(sections)) return;
  for (const section of sections) {
    if (!section.fields || !Array.isArray(section.fields)) continue;
    for (const row of section.fields) {
      if (!Array.isArray(row)) continue;
      for (const field of row) {
        resolveFieldOptions(field, dynamicLists);
      }
    }
  }
}

function resolveFieldOptions(node, dynamicLists) {
  if (!node || typeof node !== 'object') return;
  if (node.element === 'select' && Array.isArray(node.children)) {
    const hasPlaceholder = node.children.some(
      (c) => typeof c.children === 'string' && c.children.match(/^\[List of .+\]$/)
    );
    if (hasPlaceholder) {
      const placeholderChild = node.children.find(
        (c) => typeof c.children === 'string' && c.children.match(/^\[List of .+\]$/)
      );
      const key = placeholderChild.children.toLowerCase();
      let list = null;
      if (key.includes('province')) list = dynamicLists.provinces;
      else if (key.includes('insurer')) list = dynamicLists.insurers;

      if (list && list.length > 0) {
        node.children = node.children
          .filter((c) => typeof c.children !== 'string' || !c.children.match(/^\[List of .+\]$/))
          .concat(list.map((item) => ({
            element: 'option',
            props: { value: item },
            children: item,
          })));
      }
    }
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child && typeof child === 'object') {
        resolveFieldOptions(child, dynamicLists);
      }
    }
  }
}

function buildLocalDraftKey(db, fsid) {
  if (!db || !fsid) return null;
  return `${LOCAL_DRAFT_PREFIX}:${db}:${fsid}`;
}

function toTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function cloneSectionsForStorage(sections) {
  try {
    return JSON.parse(
      JSON.stringify(sections, (key, value) => {
        if (key === "fileFields") {
          return [];
        }
        if (key === "content" || typeof value === "function") {
          return undefined;
        }
        return value;
      })
    );
  } catch (error) {
    return null;
  }
}

function readLocalDraft(key) {
  if (typeof window === "undefined" || !key) return null;

  try {
    const rawDraft = window.localStorage.getItem(key);
    if (!rawDraft) return null;

    const parsedDraft = JSON.parse(rawDraft);
    if (!parsedDraft || !Array.isArray(parsedDraft.sections)) {
      return null;
    }

    return parsedDraft;
  } catch (error) {
    return null;
  }
}

function writeLocalDraft(key, draft) {
  if (typeof window === "undefined" || !key) return false;

  const serializableSections = cloneSectionsForStorage(draft.sections);
  if (!serializableSections) return false;

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        ...draft,
        sections: serializableSections,
      })
    );
    return true;
  } catch (error) {
    return false;
  }
}

function clearLocalDraft(key) {
  if (typeof window === "undefined" || !key) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Best effort: local draft cleanup should never block the form flow.
  }
}

function isFullWidthElementKey(elementKey) {
  return ["Heading", "Subheading", "Paragraph", "Popup Link"].includes(elementKey);
}

function buildTooltipIcon(tooltipText) {
  if (!tooltipText) return null;

  return React.createElement(
    Tooltip,
    {
      key: "label-tooltip",
      label: tooltipText,
      bg: "gray.800",
      color: "white",
      fontSize: "12px",
      px: 3,
      py: 2,
      borderRadius: "md",
      maxW: "250px",
      hasArrow: true,
      placement: "top",
    },
    React.createElement(
      "span",
      {
        style: {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          backgroundColor: "#718096",
          color: "white",
          fontSize: "11px",
          fontWeight: "700",
          cursor: "help",
          marginLeft: "6px",
          flexShrink: 0,
        },
      },
      "?"
    )
  );
}

function decorateLabelElement(labelElement, {required, tooltipText}) {
  if (!labelElement || (!required && !tooltipText)) {
    return labelElement;
  }

  return React.createElement(
    "span",
    {
      key: "label-decorator",
      style: {
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        rowGap: "0.125rem",
      },
    },
    labelElement,
    required
      ? React.createElement(
          "span",
          {
            key: "required-indicator",
            style: {
              color: "#E53E3E",
              fontSize: "14px",
              fontWeight: "700",
              marginLeft: "0.25rem",
            },
          },
          "*"
        )
      : null,
    buildTooltipIcon(tooltipText)
  );
}

// Lays out each checkbox option as a flex row. The required asterisk and the
// tooltip belong to the question label (children[0]), not to the options: the
// group is validated as "at least one ticked", so a per-option marker would
// read as "every option is mandatory".
function decorateCheckboxOptionLabels(children) {
  if (!Array.isArray(children) || children.length < 2) return children;

  const optionsContainer = children[1];
  if (!Array.isArray(optionsContainer?.children)) return children;

  return [
    children[0],
    {
      ...optionsContainer,
      children: optionsContainer.children.map((option) => {
        if (!Array.isArray(option?.children)) return option;

        return {
          ...option,
          props: {
            ...(option.props || {}),
            style: {
              display: "flex",
              // A box and its words are one line of meaning, so they sit on one
              // line. `flex-start` aligned the top of the square with the top of
              // the text, which puts the box ABOVE the words — reported from a
              // checklist on 2026-09-22, and true of every form this renders.
              alignItems: "center",
              gap: "0.25rem",
              marginBottom: "0.75rem",
              ...(option.props?.style || {}),
            },
          },
        };
      }),
    },
  ];
}

function isValidReactElementType(element) {
  return typeof element === "string" || typeof element === "function";
}

function summarizeFormNode(node) {
  if (node === null || node === undefined) return {kind: String(node)};
  if (React.isValidElement(node)) {
    return {
      kind: "reactElement",
      type: typeof node.type === "string" ? node.type : node.type?.name || typeof node.type,
      key: node.key,
    };
  }
  if (typeof node !== "object") {
    return {kind: typeof node, valuePreview: String(node).slice(0, 80)};
  }

  return {
    kind: "formNode",
    key: node.key,
    elementType: typeof node.element,
    element:
      typeof node.element === "string"
        ? node.element
        : node.element?.name || String(node.element).slice(0, 80),
    required: !!node.required,
    childCount: Array.isArray(node.children) ? node.children.length : undefined,
    childType: Array.isArray(node.children) ? "array" : typeof node.children,
    propKeys: node.props ? Object.keys(node.props).slice(0, 20) : [],
  };
}

function setTempFormDiagnostics(partial) {
  if (typeof window === "undefined") return;
  window.__brightlightDiagnostics = {
    ...(window.__brightlightDiagnostics || {}),
    tempForm: {
      ...(window.__brightlightDiagnostics?.tempForm || {}),
      ...partial,
      updatedAt: new Date().toISOString(),
    },
  };
}

export default function TempForm({defaultData, action}) {
  const navigate = useNavigate();
  const [data, setData] = useState(defaultData||null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sections, setSections] = useState(defaultData?.formData?.sections||[]);
  const [signatureModal, setSignatureModal] = useState(null);
  const [popupLinkData, setPopupLinkData] = useState(null);
  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [localBackupSavedAt, setLocalBackupSavedAt] = useState(null);
  const toast = useToast();
  const restoredDraftKeyRef = useRef(null);
  const skipNextLocalBackupRef = useRef(true);
  const stepperSize = useBreakpointValue({base: "sm", md: "md"});
  const layoutPx = useBreakpointValue({base: 4, md: 12, lg: 20});
  const layoutPy = useBreakpointValue({base: 6, md: 10, lg: 16});
  const contentMaxW = useBreakpointValue({base: "full", md: "56rem", lg: "60rem"});
  const localDraftKey = useMemo(
    () =>
      buildLocalDraftKey(
        data?.db || defaultData?.db,
        data?.formData?.fsid || defaultData?.formData?.fsid
      ),
    [data?.db, data?.formData?.fsid, defaultData?.db, defaultData?.formData?.fsid]
  );

  // Extract branding from form data
  const branding = useMemo(() => {
    return data?.formData?.branding || data?.branding || null;
  }, [data]);

  const primaryColor = branding?.primaryColor || "#604652";
  // Secondary brand color: the section-header band + completed-step fill,
  // mirroring how it's used on the generated PDF (FormsPDF.jsx). White text
  // sits on this band, so it assumes a reasonably dark secondary (same
  // assumption the PDF already makes).
  const secondaryColor = branding?.secondaryColor || "#4b5563";
  const logoUrl = branding?.logo || null;

  const validateUrlAndGetData = async (params) => {
    try {
      const response = await validateUrlTempForms(params);
      return response?.data;
    } catch (error) {
      // The server answers a refused link with `{error, message}` now, not a
      // bare string. The caller hands whatever comes back to `setErrorMessage`
      // and React renders it, so an object here is a blank screen where the
      // explanation should be. Same vocabulary as a refused submission — a link
      // that will not open and a link that will not submit are one situation to
      // the patient holding it.
      const body = error?.response?.data;
      if (typeof body === "string") return body;
      const described = describeSubmissionFailure(error);
      // Opened on a form that is already completed — on another device, or
      // between the app minting this link and the page loading. Inside the app
      // that is the end of this form, not an error to stare at: the app closes
      // the view and re-reads the list. The refusal names no submission, so
      // neither does the message.
      if (described.done) {
        reportFormResultToMobileApp({state: FORM_RESULT_STATES.completed});
      }
      return described.description;
    }
  };
   const steps = useMemo(() => sections.map((section, index) => ({
    label: section.name,
   })), [sections]);
   const {activeStep, setActiveStep} = useSteps({index: 0, count: steps.length});
   let activeSection = sections[activeStep];
   let activeFields = sections[activeStep]?.fields||[];
   const serverDraftSavedAt = data?.formData?.draftSavedAt || null;
   const latestDraftSavedAt = (() => {
    const localBackupTime = toTimestamp(localBackupSavedAt);
    const serverDraftTime = toTimestamp(serverDraftSavedAt);

    if (localBackupTime && (!serverDraftTime || localBackupTime >= serverDraftTime)) {
      return localBackupSavedAt;
    }

    return serverDraftSavedAt;
   })();
   const latestDraftSource = latestDraftSavedAt && latestDraftSavedAt === localBackupSavedAt
    ? "local"
    : "server";
   const latestDraftLabel = latestDraftSavedAt
    ? new Date(latestDraftSavedAt).toLocaleString(APP_LOCALE)
    : null;

  useEffect(() => {
    setTempFormDiagnostics({
      formTitle: data?.formData?.formTitle || data?.formData?.title || null,
      fsid: data?.formData?.fsid || null,
      activeStep,
      stepCount: steps.length,
      sectionName: activeSection?.name || null,
      rowCount: Array.isArray(activeFields) ? activeFields.length : 0,
      fieldCount: Array.isArray(activeFields)
        ? activeFields.reduce((count, row) => count + (Array.isArray(row) ? row.length : 0), 0)
        : 0,
      action: action || null,
    });
  }, [
    action,
    activeFields,
    activeSection?.name,
    activeStep,
    data?.formData?.formTitle,
    data?.formData?.fsid,
    data?.formData?.title,
    steps.length,
  ]);

  const clearSavedProgress = () => {
    clearLocalDraft(localDraftKey);
    setLocalBackupSavedAt(null);
  };

  const buildDateFieldKey = (sectionIndex, rowIdx, colIdx) =>
    `${sectionIndex}-${rowIdx}-${colIdx}`;

  const validateDateFields = (fields, sectionIndex) => {
    if (!Array.isArray(fields)) return true;
    let isValid = true;
    const nextErrors = {};

    fields.forEach((row, rowIdx) => {
      if (!Array.isArray(row)) return;
      row.forEach((element, colIdx) => {
        if (!element || element.key !== "Date") return;
        const value = element?.children?.[1]?.props?.value || "";
        const fieldKey = buildDateFieldKey(sectionIndex, rowIdx, colIdx);
        if (!value) {
          nextErrors[fieldKey] = "";
          return;
        }
        const parsed = normalizeDateInput(value);
        if (!parsed.isValid) {
          isValid = false;
          nextErrors[fieldKey] = parsed.error;
        } else {
          const labelChild = element?.children?.[0];
          const labelText = typeof labelChild === 'string' ? labelChild : (labelChild?.children || '');
          if (/birth|dob/i.test(typeof labelText === 'string' ? labelText : '') && new Date(value) > new Date()) {
            isValid = false;
            nextErrors[fieldKey] = "Date of birth cannot be in the future";
          } else {
            nextErrors[fieldKey] = "";
          }
        }
      });
    });

    if (Object.keys(nextErrors).length > 0) {
      setDateFieldErrors((prev) => ({...prev, ...nextErrors}));
    }

    return isValid;
  };

  const validateAllDateFields = () => {
    let allValid = true;
    const nextErrors = {};

    sections.forEach((section, sectionIdx) => {
      const fields = section?.fields || [];
      fields.forEach((row, rowIdx) => {
        if (!Array.isArray(row)) return;
        row.forEach((element, colIdx) => {
          if (!element || element.key !== "Date") return;
          const value = element?.children?.[1]?.props?.value || "";
          const fieldKey = buildDateFieldKey(sectionIdx, rowIdx, colIdx);
          if (!value) {
            nextErrors[fieldKey] = "";
            return;
          }
          const parsed = normalizeDateInput(value);
          if (!parsed.isValid) {
            allValid = false;
            nextErrors[fieldKey] = parsed.error;
          } else {
            const labelChild = element?.children?.[0];
            const labelText = typeof labelChild === 'string' ? labelChild : (labelChild?.children || '');
            if (/birth|dob/i.test(typeof labelText === 'string' ? labelText : '') && new Date(value) > new Date()) {
              allValid = false;
              nextErrors[fieldKey] = "Date of birth cannot be in the future";
            } else {
              nextErrors[fieldKey] = "";
            }
          }
        });
      });
    });

    if (Object.keys(nextErrors).length > 0) {
      setDateFieldErrors((prev) => ({...prev, ...nextErrors}));
    }

    return allValid;
  };

  const nextStep = () => {
    if (activeStep < steps.length - 1) {
      
      if (!validateDateFields(activeFields, activeStep)) {
        toast({
          title: "Error",
          description: "Please enter valid date values",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if(!checkRequiredFields(activeFields)){
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleStepClick = (targetIndex) => {
    if (targetIndex <= activeStep) {
      setActiveStep(targetIndex);
      return;
    }
    if (!validateDateFields(activeFields, activeStep)) {
      toast({
        title: "Error",
        description: "Please enter valid date values",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!checkRequiredFields(activeFields)) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setActiveStep(targetIndex);
  };

  useEffect(() => {
    const fetchFormData = async () => {
      setLoading(true);
      const rawParams = window.location.href.split("?params=")[1] || "";
      const params = decodeURIComponent(rawParams);
      const data = await validateUrlAndGetData(params);
      if (data?.formData) {
        setData(data);
        const sections = prefillSectionsWithPatientData(
          data.formData.sections || [],
          data.patient,
          data.formData.pid
        );
        if (sections.length === 0) {
          setErrorMessage("This form is not available for completion at this time. Please contact your provider.");
          setLoading(false);
          return;
        }
        setSections(sections);
      } else {
        setErrorMessage(data);
      }
      setLoading(false);
    };

   if(!defaultData){
    fetchFormData();
   }else{
    // Resolve dynamic select options (provinces/insurers) client-side
    const resolveAndSetSections = async () => {
      const formSections = defaultData?.formData?.sections || [];
      let insurerNames = [];
      if (defaultData.state) {
        try {
          const insurerRes = await crud(defaultData.state, [
            { db: defaultData.db, collection: "insurers", parameters: [{}], method: "find" },
          ]);
          insurerNames = (insurerRes?.data?.[0] || []).map((i) => i.name).filter(Boolean).sort();
        } catch (e) { /* insurers fetch is best-effort */ }
      }
      // The same guard the emailed-link path has had all along.
      //
      // A submission whose content is only in the legacy `formFields` string
      // has no `sections`, and this branch quietly set an empty list: the
      // patient opened the form and was shown a working editor with nothing
      // in it, a Submit button, and no explanation. The link path answers 422
      // with a sentence; opening the same form from inside the portal has to
      // say the same thing rather than pretend the form is blank.
      if (!Array.isArray(formSections) || formSections.length === 0) {
        setErrorMessage(
          "This form cannot be filled in here yet. Please ask the clinic to send it to you again."
        );
        return;
      }
      resolveDynamicOptions(formSections, { provinces: PROVINCES, insurers: insurerNames });
      setSections(
        prefillSectionsWithPatientData(
          formSections,
          defaultData?.patient || defaultData?.formData?.patient,
          defaultData?.formData?.pid
        )
      );
    };
    resolveAndSetSections();
    setLoading(false);
   }
  }, []);

  useEffect(() => {
    if (
      loading ||
      errorMessage ||
      !localDraftKey ||
      !data?.formData?.fsid ||
      restoredDraftKeyRef.current === localDraftKey
    ) {
      return;
    }

    restoredDraftKeyRef.current = localDraftKey;
    skipNextLocalBackupRef.current = true;

    const localDraft = readLocalDraft(localDraftKey);
    if (!localDraft) {
      setLocalBackupSavedAt(null);
      return;
    }

    setLocalBackupSavedAt(localDraft.savedAt || null);

    const localDraftTime = toTimestamp(localDraft.savedAt);
    const serverDraftTime = toTimestamp(
      data?.formData?.draftSavedAt || data?.formData?.lastModified
    );

    if (
      Array.isArray(localDraft.sections) &&
      localDraftTime &&
      (!serverDraftTime || localDraftTime > serverDraftTime)
    ) {
      setSections(localDraft.sections);
      setData((prev) =>
        prev
          ? {
              ...prev,
              formData: {
                ...prev.formData,
                sections: localDraft.sections,
              },
            }
          : prev
      );

      toast({
        title: "Draft restored",
        description: "We restored the latest answers saved on this device.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [
    data?.formData?.draftSavedAt,
    data?.formData?.fsid,
    data?.formData?.lastModified,
    errorMessage,
    loading,
    localDraftKey,
    toast,
  ]);

  useEffect(() => {
    if (loading || errorMessage || !localDraftKey || !Array.isArray(sections)) {
      return undefined;
    }

    if (skipNextLocalBackupRef.current) {
      skipNextLocalBackupRef.current = false;
      return undefined;
    }

    const backupTimer = window.setTimeout(() => {
      const savedAt = new Date().toISOString();
      const didSave = writeLocalDraft(localDraftKey, {
        db: data?.db || defaultData?.db,
        fsid: data?.formData?.fsid || defaultData?.formData?.fsid,
        savedAt,
        sections,
      });

      if (didSave) {
        setLocalBackupSavedAt(savedAt);
      }
    }, 500);

    return () => window.clearTimeout(backupTimer);
  }, [
    data?.db,
    data?.formData?.fsid,
    defaultData?.db,
    defaultData?.formData?.fsid,
    errorMessage,
    loading,
    localDraftKey,
    sections,
  ]);

  const renderElement = (obj) => {
    setTempFormDiagnostics({
      lastRenderNode: summarizeFormNode(obj),
      lastRenderStep: activeStep,
      lastRenderSection: activeSection?.name || null,
      lastRenderRow: obj?.rowIdx,
      lastRenderCol: obj?.colIdx,
    });

    if (React.isValidElement(obj)) return obj;
    if (obj === null || obj === undefined || typeof obj === "boolean") return null;
    if (typeof obj === "string" || typeof obj === "number") return obj;
    if (typeof obj !== "object") return null;

    const {element, key, required, tooltipText: rawTooltipText, props: rawProps = {}, children, rowIdx, colIdx} = obj;

    // A LABEL NOBODY WROTE IS NOT A LABEL. A field whose name is absent used to
    // render the type's own name — "Check Boxes Label", "Date Label" — which a
    // patient read as if the clinic had written it. The placeholders are gone
    // from the descriptors; this stops the empty element they leave behind from
    // taking a line of its own. Containers (array children) keep their box.
    if (
      TEXT_ONLY_ELEMENTS.has(element) &&
      (children === undefined || children === null || (typeof children === "string" && children.trim() === ""))
    ) {
      return null;
    }
    if (
      (!element || !isValidReactElementType(element)) &&
      key !== "Popup Link" &&
      key !== "Signature"
    ) {
      if (Array.isArray(children)) {
        return children.map((child, index) => (
          <React.Fragment key={child?.key || index}>
            {renderElement(child)}
          </React.Fragment>
        ));
      }

      return renderElement(children);
    }

    const tooltipText = [
      rawTooltipText,
      rawProps?.tooltipText,
      children?.[0]?.tooltipText,
      children?.[0]?.props?.tooltipText,
    ].find((value) => typeof value === "string" && value.trim().length > 0)?.trim() || "";

    let props = rawProps;
    if (element === "input" && rawProps.type === "date") {
      props = {...rawProps};
      if (!props.min) props.min = "1900-01-01";
      const labelChild = activeFields?.[rowIdx]?.[colIdx]?.children?.[0];
      const labelText = typeof labelChild === 'string' ? labelChild : (labelChild?.children || '');
      if (/birth|dob/i.test(typeof labelText === 'string' ? labelText : '')) {
        props.max = new Date().toISOString().split('T')[0];
      } else if (!props.max) {
        props.max = "2100-12-31";
      }
    }

    if (key === "Popup Link") {
      const linkText = obj.children?.[0]?.children || "Click here";
      return (
        <Text
          as="span"
          color="blue.500"
          textDecoration="underline"
          cursor="pointer"
          fontSize="1rem"
          onClick={() => setPopupLinkData({ title: obj.popupTitle || "", content: obj.popupContent || "" })}
        >
          {linkText}
        </Text>
      );
    }

    if (key == "payment-method") {
      const ogirin = [rowIdx, colIdx];
      const rawLabel = activeFields[rowIdx][colIdx]?.children?.[0] || "";
      const savedCard = activeFields[rowIdx][colIdx]?.children?.[1]?.children || "";
      const label = decorateLabelElement(
        typeof rawLabel === "object"
          ? renderElement({
              ...rawLabel,
              key: rawLabel?.key || "stripe-card-label",
              rowIdx,
              colIdx,
            })
          : rawLabel,
        {required, tooltipText}
      );

      // Authorize the SetupIntent without a session token:
      // - Portal (authenticated): the patient's jwt + fsid (backend verifies ownership).
      // - Public email link: the form's encrypted `params` from the URL.
      const formAuth = data?.state?.jwt
        ? {fsid: data?.formData?.fsid, jwt: data.state.jwt}
        : {params: decodeURIComponent((window.location.href.split("?params=")[1] || ""))};

      return (
        <FormStripeCard
          label={label}
          savedCard={savedCard}
          onSaved={(info) => setStripeCardData(info, ogirin)}
          formAuth={formAuth}
        />
      );
    }

    if (key == "Signature") {
      const ogirin = [rowIdx, colIdx];
      const rawLabel = activeFields[rowIdx][colIdx]?.children?.[0] || "";
      const signature = activeFields[rowIdx][colIdx]?.children?.[1]?.children||'';
      const label = decorateLabelElement(
        typeof rawLabel === "object"
          ? renderElement({
              ...rawLabel,
              key: rawLabel?.key || "signature-label",
              rowIdx,
              colIdx,
            })
          : rawLabel,
        {required, tooltipText}
      );

      return <Signature label={label} ogirin={ogirin}  setSignature={(value)=>setSignature(value, ogirin)} signature={signature} setShowModal={setSignatureModal} />;

    }

    // Handle Check Boxes with no options - use label as single checkbox option
    if (key === "Check Boxes" && Array.isArray(children) && children.length >= 2) {
      const optionsContainer = children[1];
      if (!Array.isArray(optionsContainer?.children) || optionsContainer.children.length === 0) {
        const labelText = typeof children[0]?.children === 'string' ? children[0].children : "I agree";
        if (optionsContainer) {
          optionsContainer.children = [{
            element: "span",
            children: [
              {
                element: "input",
                props: { type: "checkbox", name: labelText, style: { appearance: "auto", WebkitAppearance: "checkbox", width: "1rem", height: "1rem", cursor: "pointer" }, value: labelText },
              },
              {
                element: "label",
                props: { style: { color: "#555555", marginLeft: "0.25rem", marginRight: "1rem" } },
                children: labelText,
              },
            ],
          }];
        }
      }
    }

    // Ensure native checkbox inputs are visible (Tailwind preflight sets appearance: none)
    if (element === "input" && rawProps?.type === "checkbox") {
      props = {
        ...rawProps,
        checked: !!rawProps?.checked,
        onChange: rawProps?.onChange || (() => {}),
        style: {
          ...rawProps?.style,
          appearance: "auto",
          WebkitAppearance: "checkbox",
          width: "1rem",
          height: "1rem",
          cursor: "pointer",
        },
      };
    }

    let resolvedChildren =
      key === "Check Boxes"
        ? decorateCheckboxOptionLabels(children)
        : children;

    if (resolvedChildren && !Array.isArray(resolvedChildren)) {
      return React.createElement(element, {key, ...props}, resolvedChildren);
    }

    let childElements = resolvedChildren
      ? resolvedChildren.map((child, index) => {
          if (!child || typeof child !== "object" || React.isValidElement(child)) {
            return (
              <React.Fragment key={index}>
                {renderElement(child)}
              </React.Fragment>
            );
          }

          return renderElement({...child, key: child.key || index, rowIdx, colIdx});
        })
      : null;

    if (Array.isArray(childElements) && childElements.length > 0) {
      childElements[0] = decorateLabelElement(childElements[0], {
        required,
        tooltipText,
      });
    }

    return React.createElement(element, {key, ...props}, childElements);
  };

  function setSignature(signature, ogirin){
    
    const [rowIdx, colIdx] = ogirin;
   
    setSections( prevSections =>
      prevSections.map((section, index) =>{
        let newFields = section.fields;
       
        if(index == activeStep){
        return ({
            ...section,
            fields: newFields.map((_, i) =>
              i == rowIdx
                ? _.map((formElement, j) =>{
                  if(j == colIdx){
                    formElement.children[1].children= signature;
                  }
                  return formElement;
                }
                    
                  )
                : _
            ),
          })
        }
       else{
        return section;
       }
      }

      ))
  }

  function setStripeCardData(info, ogirin){
    const [rowIdx, colIdx] = ogirin;
    // Store only a non-sensitive flag. Never persist card number / token / pm id.
    const safe = info && info.saved
      ? {saved: true, last4: info.last4 || "", brand: info.brand || ""}
      : "";

    setSections( prevSections =>
      prevSections.map((section, index) =>{
        let newFields = section.fields;

        if(index == activeStep){
        return ({
            ...section,
            fields: newFields.map((_, i) =>
              i == rowIdx
                ? _.map((formElement, j) =>{
                  if(j == colIdx){
                    formElement.children[1].children = safe;
                  }
                  return formElement;
                })
                : _
            ),
          })
        }
       else{
        return section;
       }
      }
      ))
  }


  function handleInputChange(
    rowIdx,
    colIdx,
    value,
    isCheckbox = false,
    checkboxIndex = -1,
    file
  ) {
    setSections( prevSections =>
      prevSections.map((section, index) =>{
        let newFields = section.fields;
       
        if(index == activeStep){
        return ({
            ...section,
            fields: newFields.map((_, i) =>
              i == rowIdx
                ? _.map((formElement, j) =>
                    j == colIdx ? insertFormValues(formElement, value, isCheckbox, checkboxIndex, file) : formElement
                  )
                : _
            ),
          })
        }
       else{
        return section;
       }
      }
        
      ))
  
  }

  function buildSubmissionPayload(overrides = {}) {
    return {
      ...data.formData,
      sections,
      ...overrides,
    };
  }

  async function saveDraft() {
    if (!data?.formData?.fsid) return;

    setIsSavingDraft(true);
    const draftSavedAt = new Date().toISOString();
    const payload = buildSubmissionPayload({
      completed: false,
      status: "draft",
      draftSavedAt,
      lastModified: draftSavedAt,
    });

    toast.promise(
      submitForm(data.db, data.formData.fsid, payload, {
        saveAsDraft: true,
        ...submitProof(data),
      })
        .then(() => {
          clearSavedProgress();
          setData((prev) => ({
            ...prev,
            formData: {
              ...prev.formData,
              ...payload,
            },
          }));

          if (action) {
            action?.({
              fsid: data.formData.fsid,
              completed: false,
              savedAsDraft: true,
              form: payload,
            });
          }
          reportFormResultToMobileApp({
            fsid: data.formData.fsid,
            state: FORM_RESULT_STATES.draft,
          });
        })
        .catch((error) => {
          // Completed somewhere else while this page sat open. Same ending as
          // a submit that finds the form already done: nothing is left to
          // save, and inside the app the view closes.
          if (describeSubmissionFailure(error).done) {
            clearSavedProgress();
            setIsDisabled(true);
            reportFormResultToMobileApp({
              fsid: data.formData.fsid,
              state: FORM_RESULT_STATES.completed,
            });
          }
          // Rethrown so `toast.promise` still reports the outcome.
          throw error;
        })
        .finally(() => {
          setIsSavingDraft(false);
        }),
      {
        success: {
          title: "Draft saved",
          description: action
            ? "You can continue this form later from Forms."
            : "You can safely close this page and come back later with the same link.",
        },
        error: (error) => {
          const described = describeSubmissionFailure(error);
          // A draft is only ever lost against a link that no longer works, and
          // the reasons are the same ones; the fallback sentence is the only
          // part that differs.
          return described.title === "Error"
            ? {title: "Error", description: "We couldn't save your draft. Please try again."}
            : described;
        },
        loading: {title: "Saving draft...", description: "Please wait"},
      }
    );
  }

 


  // Everything we know about why a submission failed, sent to the collector
  // that already feeds CloudWatch (`source: "patientApp"`).
  //
  // That pipeline existed the whole time a 415 was failing EVERY submission for
  // thirteen days, and reported nothing — it is wired only to `ErrorBoundary`
  // (render crashes) and `installGlobalErrorHandlers` (uncaught errors and
  // unhandled rejections), and a submission failure is neither: `toast.promise`
  // consumes the rejection, so nothing was ever unhandled. A failure the user
  // is shown but the collector never hears about is how a total outage stays
  // invisible. `axios` puts the server's own explanation on `response.data`,
  // which is the difference between "try again" and "415 Unsupported media
  // type" when someone finally looks.
  function recordSubmissionFailure(error) {
    const response = error?.response;
    const failure = {
      at: new Date().toISOString(),
      fsid: data?.formData?.fsid || null,
      db: data?.db || null,
      // Which leg broke: the PDF never reached the network, or the server
      // answered and refused.
      stage: response ? "upload/submit request" : "pdf render or network",
      status: response?.status || null,
      url: error?.config?.url || null,
      serverMessage:
        (typeof response?.data === "string"
          ? response.data
          : response?.data?.message || response?.data?.error) || null,
      message: error?.message || String(error),
    };
    setTempFormDiagnostics({lastSubmissionFailure: failure});
    reportError(error, {
      tenant: data?.db || undefined,
      extra: {feature: "formSubmission", ...failure},
    });
  }

  async function completeForm() {
    if (!validateAllDateFields()) {
      toast({
        title: "Error",
        description: "Please enter valid date values",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if(!checkRequiredFields(activeFields)){
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
 
    setIsSubmitting(true);
    let payload = buildSubmissionPayload({
      completed: true,
      completedDate: new Date(),
      status: "completed",
      draftSavedAt: null,
      lastModified: new Date().toISOString(),
    });

    toast.promise(
      // Submitting is ONE call: the patient's answers reach the server, and the
      // patient is done. Nothing else stands between them and that.
      //
      // This used to render the PDF here, in the patient's own browser, and
      // upload it BEFORE the submission was marked completed — so a phone
      // running out of memory on a fifty-section form, a font that would not
      // download, an S3 blip or a MIME label could all end with a patient
      // unable to submit at all. The server now renders that PDF from the
      // answers it just stored (`forms/formSubmissionDocumentStore`), where it
      // is reproducible, inspectable, and retried if it fails. The answers are
      // the record; the PDF is a rendering of them.
      submitForm(data.db, data.formData.fsid, payload, submitProof(data))
        .then(() => {
          clearSavedProgress();
          setIsDisabled(true);
          action && action?.({
            fsid: data.formData.fsid,
            completed: true,
            form: payload,
          });
          reportFormResultToMobileApp({
            fsid: data.formData.fsid,
            state: FORM_RESULT_STATES.completed,
          });
          // Standalone /temp-form goes home; a modal closes through `action`.
          // In the app the WebView closes, so home would be a dead end.
          if (!action && !isInMobileApp()) {
            setTimeout(() => {
              navigate("/");
            }, 3000);
          }
        })
        .catch((error) => {
          // The reason has to land SOMEWHERE besides the toast. Without this the
          // failure was completely mute: no console entry, no diagnostics,
          // nothing on the submission — which is how a 415 on every single
          // upload went unnoticed while patients retried in a loop.
          recordSubmissionFailure(error);

          // A form the server says is already completed is not a failure the
          // patient can act on: their record is stored. Stop offering the
          // button and let go of the local draft, or it sits there for ever
          // claiming there is unsent work.
          // Inside the app this is the end of the form too, so the view closes
          // exactly as it does after a submit that succeeded.
          if (describeSubmissionFailure(error).done) {
            clearSavedProgress();
            setIsDisabled(true);
            reportFormResultToMobileApp({
              fsid: data.formData.fsid,
              state: FORM_RESULT_STATES.completed,
            });
          }

          // Rethrown so `toast.promise` still reports the outcome.
          throw error;
        })
        .finally(() => {
          setIsSubmitting(false);
        }),
      {
        success: {
          title: "Success",
          description: "Form submitted successfully!",
        },
        error: (error) => describeSubmissionFailure(error),
        loading: {title: "Submitting form...", description: "Please wait"},
      }
    );
  }

  function getCheckboxIndex(event) {
    let parentDiv = event.target.parentElement;
    
    while (parentDiv && parentDiv.tagName.toLowerCase() !== "div") {
      parentDiv = parentDiv.parentElement;
    }

    if (parentDiv) {
      const children = parentDiv.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        
        if (child.contains(event.target)) {
          return i;
        }
      }
    }

    return -1;
  }

  let [sigX, sigY] = signatureModal||[];

  return (
    <Box  w={"100%"} h={"100%"}>
      {signatureModal && <SignatureModal signature={signatureModal?(activeFields[sigX][sigY]?.children?.[1]?.children||''):''} setShowModal={setSignatureModal} onSave={(value)=>setSignature(value, signatureModal)} />}
      {popupLinkData && (
        <Modal isOpen={true} onClose={() => setPopupLinkData(null)} isCentered size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize="16px" fontWeight="600">{popupLinkData.title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text whiteSpace="pre-wrap" fontSize="14px" color="gray.700">{popupLinkData.content}</Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setPopupLinkData(null)} colorScheme="blue" size="sm">Okay, Got it</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {loading ? (
        <Flex
          position="absolute"
          top="0"
          left="240px"
          right="0"
          bottom="0"
          justifyContent="center"
          alignItems="center"
          bg="rgba(255, 255, 255, 0.8)"
          zIndex="1"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Flex>
      ) : errorMessage ? (
        // Sized to its container, not to the viewport, and allowed to WRAP.
        // As a 3xl heading with noOfLines={1} every message longer than a few
        // words was cut off mid-sentence — and each one of these messages is
        // the only thing telling the patient what to do next.
        <Center w="100%" minH="240px" h="100%" px={6} py={10}>
          <Text
            as="p"
            maxW="32rem"
            textAlign="center"
            fontSize={{base: "md", md: "lg"}}
            color="gray.600"
          >
            {errorMessage}
          </Text>
        </Center>
      ) : (
        <Box className="w-full h-full" >
        <VStack w={"100%"} height={"100%"} px={layoutPx} py={layoutPy} gap={4}   alignItems={'center'} >
          {logoUrl && (
            <Flex w={'full'} maxW={contentMaxW} justifyContent={'center'} mb={2}>
              <Image
                src={logoUrl}
                alt="Logo"
                maxH={{base: "56px", md: "84px"}}
                objectFit="contain"
              />
            </Flex>
          )}
          {/* Dots-only stepper for navigation — no labels, so long
              section names never overlap. */}
          {steps.length > 1 && (
          <Flex w={'full'} maxW={contentMaxW} overflowX={'auto'} overflowY={'hidden'} alignItems={'center'} justifyContent={'center'} py={1}>
           <Stepper
             orientation="horizontal"
             size={stepperSize}
             index={activeStep}
             colorScheme={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? undefined : primaryColor}
             sx={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? {
               '& .chakra-stepper__step-indicator[data-status="active"]': {
                 borderColor: primaryColor,
                 color: primaryColor,
               },
               // Completed steps fill with the secondary brand color so it reads
               // as a distinct "done" state vs. the primary "active" outline.
               '& .chakra-stepper__step-indicator[data-status="complete"]': {
                bg: secondaryColor,
                borderColor: secondaryColor,
              },
            } : {}}
           >
              {steps.map((_, index) => (
                <Step key={index} onClick={() => handleStepClick(index)} cursor={'pointer'}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </Flex>
          )}
          <Flex
            w={'100%'}
            flex={1}
            maxW={contentMaxW}
            overflow={'hidden'}
            bg={'white'}
            borderWidth="1px"
            borderColor="gray.200"
            rounded={'xl'}
            shadow={'sm'}
            mx={'auto'}
            flexDirection={'column'}
            sx={{
              "input:not([type=checkbox]):not([type=radio]), select, textarea": {
                borderWidth: "1px",
                borderColor: "gray.300",
                borderRadius: "md",
                bg: "white",
                px: 3,
                py: 2,
                transition: "all 0.15s ease",
              },
              "input:not([type=checkbox]):not([type=radio]):focus, select:focus, textarea:focus":
                {
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 1px ${primaryColor}`,
                  outline: "none",
                },
            }}
          >
            {/* Section-header band in the secondary brand color (white text),
                matching the generated PDF. */}
            {activeSection?.name && (
              <Flex
                w="100%"
                bg={secondaryColor}
                px={{base: 5, md: 8}}
                py={{base: 3, md: 4}}
                align="center"
                justify="space-between"
                gap={3}
              >
                <Text
                  color="white"
                  fontWeight="700"
                  fontSize={{base: "md", md: "lg"}}
                  lineHeight="short"
                  overflowWrap="break-word"
                >
                  {activeSection.name}
                </Text>
                {steps.length > 1 && (
                  <Text
                    color="whiteAlpha.800"
                    fontSize="sm"
                    fontWeight="600"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    Step {activeStep + 1} of {steps.length}
                  </Text>
                )}
              </Flex>
            )}

          <Box w={'100%'} flex={1} minH={0} overflowY="auto" px={{base: 5, md: 8}} py={{base: 5, md: 8}}
                >
            {activeFields?.map((row, rowIdx) => {
              const totalColumns = row.length || 1;
              const mdColumns = Math.min(2, totalColumns);
              const lgColumns = Math.min(totalColumns, MAX_FORM_ROW_COLUMNS);
              return (
                <SimpleGrid
                  key={`${rowIdx}-${activeSection.name}`}
                  columns={{base: 1, md: mdColumns, lg: lgColumns}}
                  spacing={{base: 3, md: 4}}
                  w="100%"
                  px={{base: 0, md: 2}}
                  mb={{base: 6, md: 8}}
                  >
                  {row?.map((element, colIdx) => {
                    const {key, props = {}, children} =
                      element && typeof element === "object" && !React.isValidElement(element)
                        ? element
                        : {};
                    const dateFieldKey = buildDateFieldKey(activeStep, rowIdx, colIdx);
                    const dateErrorMessage = dateFieldErrors[dateFieldKey];
                    const spanFullWidth = isFullWidthElementKey(element?.key);
                    return (
                      <Box
                        key={`${rowIdx}-${colIdx}`}
                        w="100%"
                        display="flex"
                        flexDirection="column"
                        gridColumn={spanFullWidth ? {base: "auto", md: "1 / -1"} : undefined}
                        sx={{
                          // Bottom-align the field control so dropdowns/inputs
                          // line up across a row even when the question labels
                          // wrap to different numbers of lines. The grid already
                          // makes paired cells equal height; the field root
                          // fills it and the control is pushed to the bottom, so
                          // the gap under each question varies with its length.
                          "& > div": {
                            flex: "1",
                            display: "flex",
                            flexDirection: "column",
                          },
                          "& select, & textarea, & input:not([type=checkbox]):not([type=radio])":
                            {marginTop: "auto"},
                        }}
                      >
                        {element &&
                          renderElement(
                            element && typeof element === "object" && !React.isValidElement(element)
                              ? {
                                  ...element,
                            rowIdx,
                            colIdx,
                            props: {
                              ...props,
                              onChange: (e) => {
                                
                                const isCheckbox = e.target.type === "checkbox";
                                const checkboxIndex = isCheckbox
                                  ? getCheckboxIndex(e)
                                  : -1;
                                const isDateField = element?.key === "Date" || e.target.type === "date";
                                if (isDateField) {
                                  const nextValue = e.target.value;
                                  if (!nextValue) {
                                    setDateFieldErrors((prev) => ({
                                      ...prev,
                                      [dateFieldKey]: "",
                                    }));
                                  } else {
                                    const parsed = normalizeDateInput(nextValue);
                                    setDateFieldErrors((prev) => ({
                                      ...prev,
                                      [dateFieldKey]: parsed.isValid ? "" : parsed.error,
                                    }));
                                  }
                                }
                                handleInputChange(
                                  rowIdx,
                                  colIdx,
                                  e.target.value,
                                  isCheckbox,
                                  checkboxIndex,
                                  e.target.files?.[0]
                                );
                              },
                                  },
                                }
                              : element
                          )}
                        {element?.key === "Date" && dateErrorMessage && (
                          <Text fontSize="xs" color="red.500" mt={1}>
                            {dateErrorMessage}
                          </Text>
                        )}
                      </Box>
                    );
                  })}
                </SimpleGrid>
              );
            })}
          </Box>
         
            
           
          </Flex>
          <Flex
            w="100%"
            maxW={contentMaxW}
            justify="space-between"
            mt={4}
            gap={{base: 3, md: 4}}
            flexDirection={{base: "column", sm: "row"}}
            alignItems={{base: "stretch", sm: "center"}}
          >
              {latestDraftLabel ? (
                <Text fontSize="12px" color="gray.500" textAlign={{base: "center", sm: "left"}}>
                  {latestDraftSource === "local"
                    ? `Saved on this device ${latestDraftLabel}`
                    : `Draft saved ${latestDraftLabel}`}
                </Text>
              ) : (
                <Box display={{base: "none", sm: "block"}} />
              )}
              <Flex
                gap={{base: 3, sm: 3}}
                flexDirection={{base: "column", sm: "row"}}
                w={{base: "100%", sm: "auto"}}
              >
              <Button
                onClick={prevStep}
                isDisabled={activeStep === 0}
                colorScheme={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? undefined : primaryColor}
                variant="outline"
                leftIcon={<ArrowLeftIcon />}
                w={{base: "100%", sm: "auto"}}
                sx={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? {
                  borderColor: primaryColor,
                  color: primaryColor,
                  _hover: {
                    bg: `${primaryColor}10`,
                  },
                } : {}}
              > 
                Previous
              </Button>
              <Button
                onClick={saveDraft}
                isLoading={isSavingDraft}
                isDisabled={isDisabled || isSubmitting}
                variant="ghost"
                color="gray.700"
                w={{base: "100%", sm: "auto"}}
              >
                Save Draft
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                    type="submit"
                    isLoading={isSubmitting}
                  isDisabled={isDisabled}
                  colorScheme={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? undefined : primaryColor}
                  onClick={completeForm}
                  w={{base: "100%", sm: "auto"}}
                  sx={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? {
                    bg: primaryColor,
                    color: "white",
                    _hover: {
                      bg: primaryColor,
                      opacity: 0.9,
                    },
                    _disabled: {
                      bg: primaryColor,
                      opacity: 0.6,
                    },
                  } : {}}
                >
                  Complete Form
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  colorScheme={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? undefined : primaryColor}
                  rightIcon={<ArrowRightIcon />}
                  w={{base: "100%", sm: "auto"}}
                  sx={typeof primaryColor === 'string' && primaryColor.match(/^#[0-9A-Fa-f]{6}$/) ? {
                    bg: primaryColor,
                    color: "white",
                    _hover: {
                      bg: primaryColor,
                      opacity: 0.9,
                    },
                  } : {}}
                >
                  Next
                </Button>
              )}
              </Flex>
            </Flex>
        </VStack>
        </Box>
      )}
    </Box>
  );
}


function insertFormValues(formElement, value, isCheckbox = false, checkboxIndex = -1, file){
          

  if (
    formElement.element === "input" &&
    formElement.props.type === "checkbox"
  ) {
    formElement.props.checked = !formElement.props.checked;
  } else if (
    formElement.element === "input" &&
    formElement.props.type !== "checkbox" &&
    formElement.props.type !== "file"
  ) {
    formElement.props.value = value;
    delete formElement.props['defaultValue'];
  } else if (formElement.element === "textarea") {
    formElement.props.value = value;
    delete formElement.props['defaultValue'];
  } else if (formElement.element === "select") {
    formElement.props.value = value;
    delete formElement.props['defaultValue'];
  } else if (formElement.element === "div" && formElement.children) {
    formElement.children.forEach((child) => {
      if (
        child.element === "input" &&
        child.props.type !== "checkbox" &&
        child.props.type !== "file"
      ) {
        child.props.value = value;
        delete child.props['defaultValue'];
      } else if (child.element === "textarea") {
        child.props.value = value;
        delete child.props['defaultValue'];
      } else if (child.element === "select") {
        child.props.value = value;
        delete child.props['defaultValue'];
      } else if (child.element === "input" && child.props.type === "file") {
        if (file) {
          const reader = new FileReader();
          reader.onload = function (event) {
            const base64Content = event.target.result.split(",")[1];
            const fileFields = {
              fieldId: uuidv4(),
              fileName: file.name,
              content: base64Content,
            };
            child.props.fileFields = [fileFields];
          };
          reader.readAsDataURL(file);
        }
      } else if (child.element === "div" && child.children) {
        child.children.forEach((grandchild) => {
          if (
            grandchild.element === "input" &&
            grandchild.props.type !== "checkbox" &&
            grandchild.props.type !== "file"
          ) {
            grandchild.props.value = value;
          } else if (grandchild.element === "textarea") {
            grandchild.props.value = value;
            delete grandchild.props['defaultValue'];
          } else if (grandchild.element === "select") {
            grandchild.props.value = value;
            delete grandchild.props['defaultValue'];
          }
        });
      }
    });
    if (isCheckbox && formElement.children) {
      toggleCheckboxInput(formElement, checkboxIndex);
    }
  }
  return formElement;
};

function checkRequiredFields(fields) {

  for(let row of fields){
    for(let element of row){
      if(element.required){
        let key = element.key;
        let value = '';
        switch (key) {
          case "Text":
          case "Note":
          case "Select Menu":
          case "Date":
          case "Slider":
          case "Signature":
          case "File Upload":{
            if(key == "File Upload"){
               value = element.children[1].props.fileFields?.[0]?.fileName;
            }else if(key == "Signature"){
              value = element.children[1].children;
            }else{
              value = element.children[1].props.value;
            }

            if(!value){
             return false;
            }
          }
          break;
          case "payment-method":{
            value = element.children[1].children;
            if(!value || typeof value !== "object" || value.saved !== true){
              return false;
            }
          }
          break;
          case "Check Boxes":{
            if (!fieldHasValue(element)) {
              return false;
            }
            break;
          }
          default:
            break;
        }
      }
    }
  }
  return true;
}
